import type { TaskListMode } from "../../domain/app-types.ts";
import type { CollectionViewMode } from "../../domain/entities.ts";
import type { CollapsedBoardColumns } from "./board-columns.ts";
import type { TaskCollectionState } from "./task-collection-state.ts";
import { normalizeTablePreferences, type TablePreferences } from "./table/preferences.ts";

export interface TaskCollectionPreferenceScope {
  activeRootId: string | null;
  activeCollectionId: string | null;
}

export interface TaskCollectionPreferenceStorage {
  read<T>(key: string, fallback: T): Promise<T>;
  write(key: string, value: unknown): Promise<void>;
}

export function taskCollectionPreferenceKey(prefix: string, scope: TaskCollectionPreferenceScope): string {
  return `${prefix}:${scope.activeRootId || "none"}:${scope.activeCollectionId || "all"}`;
}

export function createTaskCollectionPreferences(scope: TaskCollectionPreferenceScope, state: TaskCollectionState, storage: TaskCollectionPreferenceStorage) {
  let tableScope: string | null = null;
  async function ensureTablePreferences(force = false): Promise<void> {
    const key = taskCollectionPreferenceKey("taskTable", scope);
    if (!force && key === tableScope) return;
    const inCollection = !!scope.activeCollectionId;
    const [layout, table] = await Promise.all([storage.read<unknown>(`taskLayout:${scope.activeRootId || "none"}`, "list"), storage.read<unknown>(key, null)]);
    if (key !== taskCollectionPreferenceKey("taskTable", scope)) return;
    state.listLayout = layout === "table" ? "table" : "list";
    state.table = normalizeTablePreferences(table, inCollection);
    if (scope.activeRootId === "cross-space" && table === null) state.table.columns = ["space", ...state.table.columns];
    tableScope = key;
  }
  return {
    ensureTablePreferences,
    async restore(): Promise<void> {
      const timeline=await storage.read<unknown>("timelineUnscheduledOpen",{});
      state.timelineUnscheduledOpen=timeline&&typeof timeline==="object"&&!Array.isArray(timeline)?Object.fromEntries(Object.entries(timeline).filter(([,value])=>typeof value==="boolean").slice(-1000)):{};
      const stored = await storage.read<TaskListMode | "titles">(taskCollectionPreferenceKey("taskListMode", scope), state.listMode);
      state.listMode = stored === "titles" ? "simple" : stored;
      state.boardMode = await storage.read(taskCollectionPreferenceKey("boardCardMode", scope), state.boardMode);
      state.setInactiveDays(await storage.read(taskCollectionPreferenceKey("boardInactiveDays", scope), 0));
      const view = scope.activeCollectionId ? await storage.read<CollectionViewMode | null>(taskCollectionPreferenceKey("collectionView", scope), null) : null;
      state.selectedView = view === "list" || view === "board" || view === "timeline" || view === "table" ? view : null;
      await ensureTablePreferences(true);
    },

    async setCollectionView(view: CollectionViewMode): Promise<void> {
      if (!scope.activeCollectionId) return;
      state.selectedView = view;
      await storage.write(taskCollectionPreferenceKey("collectionView", scope), view);
    },

    async setListLayout(view: "list" | "table"): Promise<void> {
      const rootId = scope.activeRootId;
      await storage.write(`taskLayout:${rootId || "none"}`, view);
      if (scope.activeRootId === rootId) state.listLayout = view;
    },

    async setTablePreferences(value: TablePreferences): Promise<void> {
      const key = taskCollectionPreferenceKey("taskTable", scope);
      const normalized = normalizeTablePreferences(value, !!scope.activeCollectionId);
      await storage.write(key, normalized);
      if (key === taskCollectionPreferenceKey("taskTable", scope)) state.table = normalized;
    },

    async setListMode(mode: TaskListMode): Promise<void> {
      state.listMode = mode;
      await storage.write("taskListMode", mode);
      await storage.write(taskCollectionPreferenceKey("taskListMode", scope), mode);
    },

    async setBoardMode(mode: "simple" | "detailed"): Promise<void> {
      state.boardMode = mode;
      await storage.write(taskCollectionPreferenceKey("boardCardMode", scope), mode);
    },

    async setInactiveDays(value: unknown): Promise<void> {
      const days = state.setInactiveDays(value);
      await storage.write(taskCollectionPreferenceKey("boardInactiveDays", scope), days);
    },

    async setCollapsedColumns(value: CollapsedBoardColumns): Promise<void> {
      state.collapsedColumns = value;
      await storage.write("collapsedBoardColumns", value);
    },
  };
}
